/**
 * @fileoverview Enhanced HTTP client with quota detection and error handling
 * Extends the existing API interceptor to handle quota exceeded responses
 */

import { QuotaExceededError, normalizeQuotaPayload } from './quota.js';
import { apiRequest as originalApiRequest } from '../services/apiInterceptor.js';

/**
 * Enhanced API request function that detects and handles quota exceeded responses
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} Response data
 * @throws {QuotaExceededError} When quota is exceeded (HTTP 403 with quota payload)
 */
async function request(endpoint, options = {}) {
  try {
    return await originalApiRequest(endpoint, options);
  } catch (error) {
    // Check if this is a 403 error that might be quota-related
    if (error && error.status === 403) {
      // Try to extract quota information from the error
      let quotaPayload = null;
      
      // Check various places where the quota payload might be
      if (error.data) {
        quotaPayload = error.data;
      } else if (error.response && error.response.data) {
        quotaPayload = error.response.data;
      } else if (error.body) {
        quotaPayload = error.body;
      }
      
      // If we have a payload that looks like a quota error, convert it
      if (quotaPayload && 
          (quotaPayload.title === 'Plan Limit Exceeded' || 
           quotaPayload.detail || // Include any response with detail field
           quotaPayload.usageMetrics ||
           quotaPayload.UsageMetrics || // Handle capital case from API
           quotaPayload.limit !== undefined ||
           quotaPayload.usage !== undefined ||
           (quotaPayload.status === 403 && quotaPayload.detail))) { // Explicit 403 with detail
        
        const normalizedQuota = normalizeQuotaPayload(quotaPayload);
        // Add endpoint context to help with debugging and user messages
        normalizedQuota.endpoint = endpoint;
        
        throw new QuotaExceededError(normalizedQuota);
      }
    }
    
    // Re-throw the original error if it's not quota-related
    throw error;
  }
}

/**
 * HTTP client wrapper with quota detection
 * Provides convenient methods for different HTTP verbs
 */
const http = {
  /**
   * GET request with quota detection
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Promise<any>} Response data
   */
  async get(endpoint, params = {}) {
    const queryString = createQueryString(params);
    return request(`${endpoint}${queryString}`, { method: 'GET' });
  },

  /**
   * POST request with quota detection
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request body data
   * @returns {Promise<any>} Response data
   */
  async post(endpoint, data) {
    return request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },

  /**
   * PUT request with quota detection
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request body data
   * @returns {Promise<any>} Response data
   */
  async put(endpoint, data) {
    return request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },

  /**
   * DELETE request with quota detection
   * @param {string} endpoint - API endpoint
   * @returns {Promise<any>} Response data
   */
  async delete(endpoint) {
    return request(endpoint, { method: 'DELETE' });
  },

  /**
   * PATCH request with quota detection
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request body data
   * @returns {Promise<any>} Response data
   */
  async patch(endpoint, data) {
    return request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }
};

/**
 * Creates query string from parameters object
 * @param {Object} params - Object containing query parameters
 * @returns {string} - Formatted query string
 */
function createQueryString(params) {
  if (!params || Object.keys(params).length === 0) return '';
  
  const queryParams = [];
  
  for (const [key, value] of Object.entries(params)) {
    // Skip null or undefined values
    if (value === null || value === undefined) continue;
    
    // Handle arrays
    if (Array.isArray(value)) {
      value.forEach(item => {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
      });
    } else {
      queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  
  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

export { request, http as default };
